import {
  getCurrentFacilityId,
  useAuth,
} from "@risalabs_frontend_org/auth-wrapper";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LoaderMessage } from "../components/loaderMessage/loaderMessage";
import { LocalStorageKeys } from "../enums/localStorageKeys";
import { loginSuccess, resetAllStates } from "../redux/slice/auth-slice";
import { fetchauthStatusOptions } from "../redux/slice/authStatusOptionsSlice";
import { fetchBOOptions } from "../redux/slice/boOptionsSlice";
import { fetchAllCmmFormConfiguration } from "../redux/slice/cmm/cmmFormConfig";
import {
  fetchFacilityPlansFromFirebase,
  resetFacilityPlan,
} from "../redux/slice/facilitySlice";
import { fetchAllFormOptionsFromFirebase } from "../redux/slice/formOptionsSlice";
import {
  getProviderDetailsFor,
  resetProviderDetails,
} from "../redux/slice/providerDetailsSlice";
import { AppDispatch, RootState } from "../redux/store/store";
import {
  clearAllFromLocalStorage,
  setItemInLocalStorage,
} from "../utils/localStorageHelper";
import { getOrgIdForFetchExternalWorklist } from "../utils/organizationHelper";
import { isProviderForUser } from "../utils/providerIdentity";

/**
 * Bridges auth-wrapper authentication state into the pharmacy dashboard's
 * own Redux store and localStorage, and gates `children` until the critical
 * post-auth slices (providerDetails, facilitySlice, authStatusOptions) are loaded.
 *
 * If a slice errors or the bootstrap exceeds BOOTSTRAP_TIMEOUT_MS, children render
 * anyway so the user is never trapped.
 */
const BOOTSTRAP_TIMEOUT_MS = 5000;

const AuthDataBootstrap = ({ children }: { children: ReactNode }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated, isLoading, user, facility } = useAuth();

  const { data: providerDetails, error: providerError } = useSelector(
    (state: RootState) => state.providerDetails,
  );
  const { data: facilityPlanDetails, error: facilityError } = useSelector(
    (state: RootState) => state.facilitySlice,
  );
  const { data: authStatusOptions, error: authStatusOptionsError } =
    useSelector((state: RootState) => state.authStatusOptions);

  const isProviderForCurrentUser = isProviderForUser(providerDetails, user?.id);
  const providerFacilityId = isProviderForCurrentUser
    ? providerDetails?.FacilityId
    : "";

  const hasFetchedGlobalConfigRef = useRef(false);
  const lastFacilityBootstrappedRef = useRef<string>("");
  const lastOptionsFacilityRef = useRef<string>("");

  const [bootstrapTimedOut, setBootstrapTimedOut] = useState(false);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (!isAuthenticated || !user?.id) {
      hasFetchedGlobalConfigRef.current = false;
      lastFacilityBootstrappedRef.current = "";
      lastOptionsFacilityRef.current = "";
      dispatch(resetAllStates());
      dispatch(resetProviderDetails());
      dispatch(resetFacilityPlan());
      clearAllFromLocalStorage();
      return;
    }

    dispatch(
      loginSuccess({
        email: user.email,
        id: user.id,
      }),
    );
    setItemInLocalStorage(LocalStorageKeys.USER_ID, user.id);

    if (!hasFetchedGlobalConfigRef.current) {
      hasFetchedGlobalConfigRef.current = true;
      dispatch(fetchAllFormOptionsFromFirebase());
      dispatch(fetchAllCmmFormConfiguration());
    }

    if (!isProviderForUser(providerDetails, user.id)) {
      dispatch(getProviderDetailsFor(user.id));
    }
  }, [dispatch, isAuthenticated, isLoading, providerDetails, user]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const resolvedFacilityId =
      facility?.id || providerFacilityId || getCurrentFacilityId() || "";

    if (
      !resolvedFacilityId ||
      lastFacilityBootstrappedRef.current === resolvedFacilityId
    ) {
      return;
    }

    lastFacilityBootstrappedRef.current = resolvedFacilityId;
    setItemInLocalStorage(
      LocalStorageKeys.HEALTHCARE_FACILITY_ID,
      resolvedFacilityId,
    );
    dispatch(fetchFacilityPlansFromFirebase(resolvedFacilityId));
  }, [dispatch, facility?.id, isAuthenticated, providerFacilityId]);

  useEffect(() => {
    if (!isAuthenticated || !facilityPlanDetails) {
      return;
    }

    if (facilityPlanDetails.feature) {
      setItemInLocalStorage(
        LocalStorageKeys.FEATURES,
        facilityPlanDetails.feature,
      );
    }

    const optionsFacilityId =
      getOrgIdForFetchExternalWorklist() ||
      getCurrentFacilityId() ||
      providerFacilityId ||
      "";

    if (
      !optionsFacilityId ||
      lastOptionsFacilityRef.current === optionsFacilityId
    ) {
      return;
    }

    lastOptionsFacilityRef.current = optionsFacilityId;
    dispatch(fetchBOOptions(optionsFacilityId));
    dispatch(fetchauthStatusOptions(optionsFacilityId));
  }, [dispatch, isAuthenticated, providerFacilityId, facilityPlanDetails]);

  useEffect(() => {
    if (!isAuthenticated) {
      setBootstrapTimedOut(false);
      return;
    }
    const id = window.setTimeout(
      () => setBootstrapTimedOut(true),
      BOOTSTRAP_TIMEOUT_MS,
    );
    return () => window.clearTimeout(id);
  }, [isAuthenticated]);

  const criticalDataLoaded =
    Boolean(providerDetails) &&
    Boolean(facilityPlanDetails) &&
    authStatusOptions.length > 0;
  const criticalDataErrored =
    Boolean(providerError) ||
    Boolean(facilityError) ||
    Boolean(authStatusOptionsError);
  const showLoadingScreen =
    isAuthenticated &&
    !criticalDataLoaded &&
    !criticalDataErrored &&
    !bootstrapTimedOut;

  if (showLoadingScreen) {
    return (
      <div style={{ height: "100vh", width: "100vw" }}>
        <LoaderMessage message="Loading your dashboard..." />
      </div>
    );
  }

  return <>{children}</>;
};

export default AuthDataBootstrap;
